import { ActionsFooter, Popup } from "component-library";
import { UserClaimTypes, UserManager } from "core";
import {
  IAgencyList,
  ICastingNotes,
  INewShortlist,
  IShortlistPerformer,
  IShortlists,
} from "models";
import { ActionsFooterRow } from "pages/Profile/components/ActionFooterSection/ActionsFooterRow";
import {
  useLoadAgencies,
  useLoadCastingNotes,
  useLoadShortList,
} from "pages/Profile/components/ActionFooterSection/helper";
import ShareViaEmail from "pages/Profile/components/common/ShareViaEmail";
import { sendEmail } from "pages/Profile/helpers/sendEmail";
import { useGetTranslationContent } from "pages/Profile/hooks/useGetTranslationContent";
import { useEffect, useState } from "react";
import CastingDirectorService from "services/CastingDirectorService";
import ShortlistService from "services/ShortlistService";
import { getUserAttribute, UserAttributes } from "shared-auth";
import { captureExceptionEvent } from "shared-services";

const ActionFooterSection = () => {
  const [shortList, setShortList] = useState<IShortlists | undefined>();
  const [agencies, setAgencies] = useState<IAgencyList | undefined>();
  const [castingNotes, setCastingNotes] = useState<ICastingNotes | undefined>();
  const [isEmailModalVisible, setIsEmailModalVisible] = useState(false);

  const content = useGetTranslationContent();

  const {
    data: fetchedShortList,
    refetch: reloadShortList,
    isLoading: isShortListLoading,
  } = useLoadShortList();
  const { data: fetchedAgencies, isLoading: isAgenciesLoading } =
    useLoadAgencies();
  const { data: fetchedCastingNotes, isLoading: isCastingNotesLoading } =
    useLoadCastingNotes();

  const isLoading =
    isShortListLoading || isAgenciesLoading || isCastingNotesLoading;

  const printPage = () => {
    try {
      document.execCommand("print");
    } catch (error) {
      window.print();
    }
  };

  const closeEmailPopup = () => setIsEmailModalVisible(false);

  const openEmailPopup = () => setIsEmailModalVisible(true);

  const saveNotes = async (notes: ICastingNotes): Promise<void> => {
    const userId = UserManager.getClaimWithDefault(UserClaimTypes.userId);
    const masterPortalGuid = getUserAttribute(UserAttributes.MasterPortalGuid);

    try {
      const castingNotes = await CastingDirectorService.saveNotes(
        userId,
        masterPortalGuid,
        notes
      );

      setCastingNotes(castingNotes);
    } catch (error) {
      captureExceptionEvent(error);
    }
  };

  const addShortList = async (model: INewShortlist): Promise<string> => {
    let linkToShortlist = "";

    try {
      const {
        _links: {
          shortlist: { href },
        },
      } = await ShortlistService.addShortList(model);

      linkToShortlist = href;
    } catch (error) {
      captureExceptionEvent(error);
    }

    await reloadShortList();

    return linkToShortlist;
  };

  const addPerformerShortList = async (
    shortlistId: number,
    model: IShortlistPerformer
  ): Promise<string> => {
    let linkToShortlist = "";

    try {
      const {
        _links: {
          shortlist: { href },
        },
      } = await ShortlistService.addPerformerShortList(shortlistId, model);
      linkToShortlist = href;
    } catch (error) {
      captureExceptionEvent(error);
    }

    await reloadShortList();

    return linkToShortlist;
  };

  useEffect(() => {
    setShortList(fetchedShortList);
    setAgencies(fetchedAgencies);
    setCastingNotes(fetchedCastingNotes);
  }, [fetchedShortList, fetchedAgencies, fetchedCastingNotes]);

  if (isLoading) {
    return null;
  }

  return (
    <>
      <ActionsFooter
        renderControl={() => (
          <ActionsFooterRow
            content={content.castingNotes}
            shortlists={shortList}
            agencies={agencies}
            notes={castingNotes}
            printPage={printPage}
            sendEmail={openEmailPopup}
            saveNotes={saveNotes}
            addShortList={addShortList}
            addPerformerShortList={addPerformerShortList}
          />
        )}
        variant="primary"
        isSticky
        fullWidth={true}
      />
      {isEmailModalVisible && (
        <Popup
          width={{
            lg: 6,
            md: 8,
          }}
          priority="medium"
          close={closeEmailPopup}
        >
          <ShareViaEmail
            content={content.emailForm}
            sendEmail={sendEmail}
            closeForm={closeEmailPopup}
          />
        </Popup>
      )}
    </>
  );
};

export default ActionFooterSection;
